import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from "@mui/material";

const normalizeValue = (value) => String(value ?? "").trim();
const normalizeKey = (value) => normalizeValue(value).toLowerCase();

const toStrings = (options = []) =>
  (options || [])
    .map((option) =>
      typeof option === "string" ? option : option?.name ?? option?.label ?? option?.id ?? ""
    )
    .map(normalizeValue)
    .filter(Boolean);

const buildSelectionKey = (color, size) => `${normalizeKey(color)}::${normalizeKey(size)}`;

const AddVariationsFlyout = ({
  open,
  anchorEl,
  colorOptions = [],
  sizeOptions = [],
  variationOptions = [],
  onClose,
  onAdd,
}) => {
  const [activeColor, setActiveColor] = useState("");
  const [selectedByColor, setSelectedByColor] = useState({});

  const mappedVariationOptions = useMemo(() => {
    const variationMap = new Map();

    (variationOptions || []).forEach((variation) => {
      const color = normalizeValue(variation?.color);
      const size = normalizeValue(variation?.size);

      if (!color || !size) return;

      const key = buildSelectionKey(color, size);
      const existing = variationMap.get(key);

      if (!existing) {
        variationMap.set(key, {
          color,
          size,
          sku: normalizeValue(variation?.sku),
        });
        return;
      }

      if (!existing.sku && variation?.sku) {
        variationMap.set(key, {
          ...existing,
          sku: normalizeValue(variation.sku),
        });
      }
    });

    return Array.from(variationMap.values());
  }, [variationOptions]);

  const hasMappedVariations = mappedVariationOptions.length > 0;

  const availableColors = useMemo(() => {
    const colorMap = new Map();
    const source = hasMappedVariations
      ? mappedVariationOptions.map((variation) => variation.color)
      : toStrings(colorOptions);

    source.forEach((color) => {
      const normalized = normalizeKey(color);
      if (!normalized || colorMap.has(normalized)) return;
      colorMap.set(normalized, color);
    });

    return Array.from(colorMap.values());
  }, [colorOptions, hasMappedVariations, mappedVariationOptions]);

  const availableSizesForActiveColor = useMemo(() => {
    if (!activeColor) return [];

    if (!hasMappedVariations) {
      return toStrings(sizeOptions);
    }

    const sizeMap = new Map();
    mappedVariationOptions.forEach((variation) => {
      if (normalizeKey(variation.color) !== normalizeKey(activeColor)) return;
      const normalized = normalizeKey(variation.size);
      if (!normalized || sizeMap.has(normalized)) return;
      sizeMap.set(normalized, variation.size);
    });

    return Array.from(sizeMap.values());
  }, [activeColor, hasMappedVariations, mappedVariationOptions, sizeOptions]);

  const totalSelected = useMemo(
    () =>
      Object.values(selectedByColor).reduce((count, sizes) => {
        const unique = new Set((sizes || []).map(normalizeKey).filter(Boolean));
        return count + unique.size;
      }, 0),
    [selectedByColor]
  );

  useEffect(() => {
    if (!open) return;
    if (availableColors.length === 0) {
      setActiveColor("");
      return;
    }

    if (!availableColors.some((color) => normalizeKey(color) === normalizeKey(activeColor))) {
      setActiveColor(availableColors[0]);
    }
  }, [activeColor, availableColors, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedByColor({});
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  const handleClose = () => {
    setSelectedByColor({});
    onClose?.();
  };

  const handleToggleSize = (color, size) => {
    setSelectedByColor((current) => {
      const normalizedSize = normalizeKey(size);
      const currentSizes = current[color] || [];
      const exists = currentSizes.some((item) => normalizeKey(item) === normalizedSize);

      return {
        ...current,
        [color]: exists
          ? currentSizes.filter((item) => normalizeKey(item) !== normalizedSize)
          : [...currentSizes, size],
      };
    });
  };

  const buildSelectedVariations = () => {
    const selectedVariations = [];
    const pushedKeys = new Set();

    Object.entries(selectedByColor).forEach(([color, sizes]) => {
      const normalizedColor = normalizeValue(color);
      if (!normalizedColor) return;

      (sizes || []).forEach((size) => {
        const normalizedSize = normalizeValue(size);
        if (!normalizedSize) return;

        const key = buildSelectionKey(normalizedColor, normalizedSize);
        if (pushedKeys.has(key)) return;

        const mappedVariation = mappedVariationOptions.find(
          (variation) =>
            normalizeKey(variation.color) === normalizeKey(normalizedColor) &&
            normalizeKey(variation.size) === normalizeKey(normalizedSize)
        );

        pushedKeys.add(key);
        selectedVariations.push({
          color: normalizedColor,
          size: normalizedSize,
          sku: mappedVariation?.sku || "",
        });
      });
    });

    return selectedVariations;
  };

  const handleAdd = () => {
    const selectedVariations = buildSelectedVariations();
    const didAddRows = onAdd?.(selectedVariations);

    if (didAddRows) {
      setSelectedByColor({});
      onClose?.();
    }
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper
          elevation={4}
          sx={{
            mt: 1,
            width: "min(92vw, 640px)",
            maxWidth: "100%",
            maxHeight: "min(70vh, 560px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700 }}>
              Add Variations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select the colour and size combinations to add as breakdown rows.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "42%" },
                flex: { xs: 1, sm: "0 0 42%" },
                minHeight: 0,
                borderRight: { xs: "none", sm: "1px solid" },
                borderBottom: { xs: "1px solid", sm: "none" },
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 1.5, pb: 0.5, flexShrink: 0 }}>
                <Typography variant="subtitle2">Colours</Typography>
              </Box>
              <List dense disablePadding sx={{ overflowY: "auto", minHeight: 0 }}>
                {availableColors.map((color) => {
                  const selectionCount = new Set(
                    (selectedByColor[color] || []).map(normalizeKey).filter(Boolean)
                  ).size;

                  return (
                    <ListItemButton
                      key={color}
                      selected={normalizeKey(activeColor) === normalizeKey(color)}
                      onClick={() => setActiveColor(color)}
                      aria-label={`Select colour ${color}`}
                    >
                      <ListItemText
                        primary={color}
                        secondary={selectionCount > 0 ? `${selectionCount} selected` : null}
                      />
                    </ListItemButton>
                  );
                })}
                {availableColors.length === 0 && (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No colours available.
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "58%" },
                flex: { xs: 1, sm: "0 0 58%" },
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 1.5, pb: 0.5, flexShrink: 0 }}>
                <Typography variant="subtitle2">
                  Sizes{activeColor ? ` for ${activeColor}` : ""}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ overflowY: "auto", minHeight: 0 }}>
                {availableSizesForActiveColor.map((size) => {
                  const checked = (selectedByColor[activeColor] || []).some(
                    (item) => normalizeKey(item) === normalizeKey(size)
                  );

                  return (
                    <ListItemButton
                      key={`${activeColor}-${size}`}
                      onClick={() => handleToggleSize(activeColor, size)}
                      disabled={!activeColor}
                      aria-label={`Toggle size ${size}${activeColor ? ` for ${activeColor}` : ""}`}
                    >
                      <Checkbox
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-label": `${size} ${checked ? "selected" : "not selected"}` }}
                      />
                      <ListItemText primary={size} />
                    </ListItemButton>
                  );
                })}
                {availableSizesForActiveColor.length === 0 && (
                  <Box sx={{ px: 2, py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {activeColor ? "No sizes available for this colour." : "Select a colour to view sizes."}
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              p: 2,
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {totalSelected > 0 ? `${totalSelected} variation${totalSelected === 1 ? "" : "s"} selected` : "No variations selected"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
              <Button onClick={handleClose} aria-label="Cancel adding variations">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={totalSelected === 0}
                aria-label={
                  totalSelected > 0
                    ? `Apply ${totalSelected} variation${totalSelected === 1 ? "" : "s"}`
                    : "Apply variations"
                }
              >
                {totalSelected > 0
                  ? `Apply ${totalSelected} Variation${totalSelected === 1 ? "" : "s"}`
                  : "Apply Variations"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default AddVariationsFlyout;
